import { post } from "./client";

/**
 * Client for the Role Provisioning endpoints on users-api (Marketing Toolkit
 * roll-out, migration-plan.md §4–§5). CS hands over a list of emails; the scan
 * resolves each one to a user + organisation and says what a run would do,
 * and apply inserts the missing account-services roles for the approved rows
 * — creating a user + membership only where the Address Book resolves the
 * email to exactly one active organisation.
 *
 * Both endpoints ride the staff-bff discovery proxy 1:1, so the paths are the
 * users-api paths. Request and response shapes here mirror the spec verbatim:
 * a backend agent implements the other side against the same document.
 */

/**
 * Rows per `apply` request. Each chunk is one transaction for the insert rows
 * (create rows go one by one because of the external Frontegg call), and the
 * response is the only record of what each row received — so the run is sent
 * as sequential chunks rather than one oversized request that could time out
 * and lose the undo list for the whole file.
 */
export const APPLY_CHUNK_SIZE = 200;

/** One file row as sent to the scan. `firmRef` restricts the row to that firm. */
export type ScanRequestRow = {
  lineNumber: number;
  email: string;
  firmRef: string | null;
};

/**
 * Server-side outcomes, §4.2–§4.5. Client-side ones live in parseCsv.ts.
 * `owner_requires_existing_user` only appears in account_owner mode (§4.5),
 * where the Address Book create path is off.
 */
export type ScanOutcome =
  | "insert"
  | "owner_requires_existing_user"
  | "already_has_roles"
  | "covered_by_account_owner"
  | "multiple_organisations"
  | "organisation_inactive"
  | "firm_ref_mismatch"
  | "create_and_insert"
  | "user_not_found"
  | "address_book_ambiguous"
  | "address_book_no_organisation"
  | "no_membership";

/** How the Address Book resolved an email the accounts DB does not know. */
export type ScanAddressBook = {
  contactId: number;
  contactName: string;
  companies: string[];
};

/** One scan result per file row — multi-org users are never fanned out. */
export type ScanRow = {
  lineNumber: number;
  email: string;
  outcome: ScanOutcome;
  userId: string | null;
  userName: string | null;
  userStatus: string | null;
  organisationId: string | null;
  organisationName: string | null;
  firmRef: string | null;
  existingRoles: string[];
  rolesToInsert: string[];
  /** The memberships behind a `multiple_organisations` row; empty otherwise. */
  organisations: string[];
  /** How the Address Book resolved the email; null for existing-user rows. */
  addressBook: ScanAddressBook | null;
};

export type ScanCounts = Partial<Record<ScanOutcome, number>>;

export type ScanResponse = {
  requestId: string;
  roles: string[];
  counts: ScanCounts;
  rows: ScanRow[];
};

export type ApplyInsertRow = {
  lineNumber: number;
  kind: "insert";
  userId: string;
  organisationId: string;
};

export type ApplyCreateRow = {
  lineNumber: number;
  kind: "create";
  email: string;
  name: string;
  organisationId: string;
};

export type ApplyRow = ApplyInsertRow | ApplyCreateRow;

export type ApplyRowStatus =
  | "inserted"
  | "created"
  | "already_has_roles"
  | "not_a_member"
  | "already_exists"
  | "error";

/** One result per submitted apply row. */
export type ApplyRowResult = {
  lineNumber: number;
  userId: string | null;
  organisationId: string | null;
  status: ApplyRowStatus;
  rolesInserted: string[];
};

export type ApplyResponse = {
  requestId: string;
  results: ApplyRowResult[];
};

export const roleProvisioningService = {
  async scan(roles: string[], rows: ScanRequestRow[]): Promise<ScanResponse> {
    return post<ScanResponse>("/v1/admin/role-provisioning/scan", { roles, rows });
  },

  async apply(roles: string[], rows: ApplyRow[]): Promise<ApplyResponse> {
    return post<ApplyResponse>("/v1/admin/role-provisioning/apply", { roles, rows });
  },
};
